import {FC} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {CheckboxElement, TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {boolean, object, string} from 'yup';

import {getErrorMessageFromApiError} from '@shared/api';
import {Dialog, DialogTitle} from '@shared/components';
import {
  useCreateCurrencyEventSchedule,
  useGetEventTemplates,
} from '../../apiHooks';
import {EventTemplate, formatEventTemplateType} from '../../types';

interface FormValues {
  templateId: string;
  cron: string;
  runImmediately: boolean;
}

const v8nSchema = object().shape({
  templateId: string().required(),
  cron: string().required(),
  runImmediately: boolean().default(false),
});

interface Props {
  open: boolean;
  onClose: () => void;
  currencyId: string;
}

export const CreateScheduleDialog: FC<Props> = ({
  open,
  onClose,
  currencyId,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const {data: templates = [], isPending: isTemplatesPending} =
    useGetEventTemplates();
  const {mutateAsync: createSchedule, isPending: isCreating} =
    useCreateCurrencyEventSchedule();

  const {control, handleSubmit, reset} = useForm<FormValues>({
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      templateId: '',
      cron: '',
      runImmediately: false,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    try {
      await createSchedule({currencyId, ...values});
      enqueueSnackbar('Schedule created successfully', {variant: 'success'});
      handleClose();
    } catch (error) {
      const errorMessage = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not create schedule. ${errorMessage}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          title="Create schedule"
          onClose={handleClose}
          sx={{px: 3, pt: 3}}
        />

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            minWidth: 380,
            px: 3,
            pt: 1,
          }}
        >
          <Controller
            name="templateId"
            control={control}
            render={({field, fieldState}) => (
              <Autocomplete
                options={templates}
                loading={isTemplatesPending}
                getOptionLabel={option =>
                  `${option.name} (${formatEventTemplateType(option.type)})`
                }
                isOptionEqualToValue={(option, val) => option.id === val.id}
                value={templates.find(t => t.id === field.value) ?? null}
                onChange={(_, newValue: EventTemplate | null) =>
                  field.onChange(newValue?.id ?? '')
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Template"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            )}
          />

          <TextFieldElement
            name="cron"
            label="Cron expression"
            control={control}
            placeholder="*/5 * * * *"
            fullWidth
          />

          <CheckboxElement
            name="runImmediately"
            label="Run immediately"
            control={control}
          />
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 3}}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isCreating}>
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
